import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  DrawerBody,
  DrawerFooter,
  HStack,
  Text
} from '@chakra-ui/react';

import FormInput from '../../../../../components/form/FormInput';
import InputLabel from '../../../../../components/form/InputLabel';
import ButtonGroup from '../../../../../components/Modal/ModalButtonGroups/ButtonGroup';
import StackedButtonGroup from '../../../../../components/Modal/ModalButtonGroups/StackedButtonGroup';
import RequirementTemplateDropdownMenu from '../../../../../components/RequirementTemplateDropdownMenu';
import SearchableDropdownMenu from '../../../../../components/SearchableDropdownMenu';
import LoadingSpinner from '../../../../../components/Spinner';
import { CypressIds } from '../../../../../constants/cypressIds';
import { inAppText } from '../../../../../constants/inAppText';
import { Statuses } from '../../../../../constants/statuses';
import { createSuccessToast, createToast } from '../../../../../redux/modules/app';
import {
  getCompanyUsers,
  selectCompanyId,
  selectCompanyUsers
} from '../../../../../redux/modules/company';
import { emailEditorReset, setInsuranceRequestInputs } from '../../../../../redux/modules/emails';
import { createInsuranceRequirement, selectInsured } from '../../../../../redux/modules/insured';
import { insuredListSearch } from '../../../../../redux/modules/insuredList';
import { getCurrentJob } from '../../../../../redux/modules/jobs';
import { jobSearch } from '../../../../../redux/modules/jobSearch';
import { hideModal, showModal } from '../../../../../redux/modules/modal';
import { hideTray } from '../../../../../redux/modules/tray';
import { useRequirementTemplateData } from '../../../../../services/requirement-template-service';
import colors from '../../../../../styles/theme/foundation/colors';
import { getJobLabel } from '../../../../../utils/jobUtil';
import useUploadContractFlow from '../../../../TemplatesListPage/components/modals/useUploadContractFlow';
import RequestForInsuranceModal
  from '../../../../V2InsuredProfilePage/components/RecordDetailsContainer/components/RecordDetails/components/PolicyRequirements/components/RequestForInsuranceModal';

const AddToProjectForm = () => {
  const dispatch = useDispatch();

  const useFormMethods = useForm();
  const { handleSubmit, isSubmitting } = useFormMethods;

  const companyId = useSelector(selectCompanyId);

  useEffect(() => {
    dispatch(jobSearch.setBaseFilters([
      { key: 'company.id', value: companyId },
      { key: 'status', value: Statuses.Active }
    ]));

    dispatch(jobSearch.getPage());
    dispatch(getCompanyUsers());
  }, [companyId, dispatch]);

  const insured = useSelector(selectInsured);
  const jobs = useSelector(jobSearch.selectData);
  const pageInfo = useSelector(jobSearch.selectPageInfo);

  const allUsers = useSelector(selectCompanyUsers);

  const users = useMemo(() => allUsers && allUsers.map(({ userFirstName, userLastName, userId }) => ({
    userFirstName,
    userLastName,
    label: `${userFirstName } ${ userLastName}`,
    userId
  })), [allUsers]);

  const [selectedJob, setSelectedJob] = useState({ name: '', id: null });
  const [selectedWatcher, setSelectedWatcher] = useState({ label: '', userId: null });
  const [searchTerm, setSearchTerm] = useState('');
  const [userOptions, setUserOptions] = useState();
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const onUploadContract = useUploadContractFlow(companyId, setSelectedTemplate);

  useEffect(() => setUserOptions(users), [users]);

  // Pre-select the project's default requirement template in the dropdown
  const { data: jobDefaultTemplate } = useRequirementTemplateData(
    selectedJob?.defaultRequirementTemplateId,
    { enabled: !!selectedJob?.defaultRequirementTemplateId }
  );

  useEffect(() => setSelectedTemplate(jobDefaultTemplate ?? null), [jobDefaultTemplate]);

  const handleWatcherSearch = selected => {
    const searchValue = selected.toLowerCase();

    if (searchValue !== '') {
      const filteredWatchers = users.filter(user => (
        user.label.toLowerCase().includes(searchValue)));
      setUserOptions(filteredWatchers);
    } else {
      setUserOptions(users);
    }

    setSearchTerm(searchValue);
  };

  const getInsuredProfilePageUrl = requirement => (requirement
    ? `insureds/${insured.insuredId}/records/${requirement.id}`
    : `insureds/${insured.insuredId}`);

  const showEmailInsuredModal = record => {
    dispatch(showModal(
      () => <RequestForInsuranceModal insuredId={insured.insuredId} jobId={selectedJob.id} recordId={record.id} />,
      {
        title: 'Email Insurance Request to Vendor/Contractor',
        size: '4xl',
        onClose: () => {
          dispatch(hideModal());
          dispatch(setInsuranceRequestInputs({
            to: null,
            cc: null
          }));

          dispatch(emailEditorReset());
        }
      }
    ));
  };

  const showEmailModal = requirement => {
    const url = getInsuredProfilePageUrl(requirement);

    dispatch(showModal(
      () => <Box>Would you like to email the insurance request to the vendor/contractor?</Box>,
      {
        title: 'Email Insured?',
        buttons: {
          component: ButtonGroup,
          primaryButtonOptions: {
            label: 'Send Email',
            onClick: () => showEmailInsuredModal(requirement),
            dataAnalyticsId: 'email-new-insured'
          },
          secondaryButtonOptions: {
            secondaryLabel: 'Skip',
            secondaryClick: () => {
              dispatch(hideModal());
              window.open(url, '_blank');
            }
          }
        },
        onClose: () => window.open(url, '_blank')
      }
    ));
  };

  const onSubmit = async data => {
    const newRequirementData = {
      job_id: selectedJob.id,
      insured_id: insured.insuredId,
      description: data.description
      // watcher_id: selectedWatcher.userId
    };

    const requirementTemplateId = selectedTemplate?.id ?? null;

    dispatch(createToast({
      title: 'Adding Insured to Project...',
      status: 'info'
    }));

    const createdRequirement = await dispatch(createInsuranceRequirement(
      insured.insuredId,
      selectedJob.id,
      newRequirementData,
      requirementTemplateId
    ));

    if (createdRequirement) {
      dispatch(createSuccessToast({
        title: 'Insured Successfully Added to Project!',
        duration: 6000
      }));

      await dispatch(getCurrentJob(createdRequirement.job_id));

      dispatch(insuredListSearch.setPostCreateFilter());
      dispatch(insuredListSearch.getPage());
      dispatch(hideTray());

      showEmailModal(createdRequirement);
    }
  };

  const handleSkipClick = () => {
    const url = getInsuredProfilePageUrl(null);

    if (selectedJob.id) {
      dispatch(showModal(
        () => <Box>The insured has not yet been added to the selected job.</Box>,
        {
          title: 'Skip step without saving?',
          buttons: {
            component: StackedButtonGroup,
            primaryButtonOptions: {
              label: 'Confirm',
              onClick: () => {
                dispatch(hideModal());
                dispatch(hideTray());
                window.open(url, '_self');
              }
            }
          }
        }
      ));
    } else {
      dispatch(hideTray());
      window.open(url, '_self');
    }
  };

  // TODO: add record attachments
  return (
    <>
      <DrawerBody>
        {(insured && userOptions) ? (
          <form id="addInsuredToProject" onSubmit={handleSubmit(onSubmit)}>
            <Box mb="26px">
              <InputLabel labelName="Insured Name" />
              <Text color={colors.greyScale['900']} textStyle="bodyMd500">{insured.insuredCompanyName}</Text>
            </Box>
            <Box mb="26px">
              <InputLabel labelName="Select Project" />
              <SearchableDropdownMenu
                data={jobs}
                endMessage={(
                  <Text color="greyScale.500" pt={2} textAlign="center" textStyle="bodySm">
                    {`Showing All (${pageInfo.total})`}
                  </Text>
                )}
                fetchNextData={() => dispatch(jobSearch.getPage({ page: pageInfo.currentPage + 1 }))}
                hasMore={pageInfo.hasMore}
                idKey="id"
                isDisabled={false}
                labelKey={getJobLabel}
                searchBlueprint={jobSearch}
                selected={getJobLabel(selectedJob)}
                truncateMenuItems={false}
                width="400px"
                onSelect={setSelectedJob}
              />
            </Box>
            {selectedJob.id && (
            <Box>
              <FormInput
                inputName="recordNumber"
                labelName="Record Number"
                toolTip={inAppText.tooltips.insureds.newInsured.addToProject.recordNumber}
                useFormMethods={useFormMethods}
              />
              <FormInput
                inputName="recordNotes"
                labelName="Insured Record Notes"
                toolTip={inAppText.tooltips.insureds.newInsured.addToProject.insuredRecordNotes}
                type="textArea"
                useFormMethods={useFormMethods}
              />
              {/* <Box mb="26px"> */}
              {/*   <InputLabel */}
              {/*     labelName="Record Watcher" */}
              {/*     toolTip={inAppText.tooltips.insureds.newInsured.addToProject.recordWatcher} */}
              {/*   /> */}
              {/*   <SearchableDropdownMenu */}
              {/*     data={userOptions} */}
              {/*     endMessage={( */}
              {/*       <Text color="greyScale.500" pt={2} textAlign="center" textStyle="bodySm"> */}
              {/*         {`Showing All (${userOptions.length})`} */}
              {/*       </Text> */}
              {/*     )} */}
              {/*     idKey="userId" */}
              {/*     isDisabled={false} */}
              {/*     labelKey="label" */}
              {/*     searchTerm={searchTerm} */}
              {/*     selected={selectedWatcher?.label} */}
              {/*     width="400px" */}
              {/*     onSearchChange={handleWatcherSearch} */}
              {/*     onSelect={setSelectedWatcher} */}
              {/*   /> */}
              {/* </Box> */}
              <Box mb="26px">
                <InputLabel
                  labelName="Insurance Requirements"
                  toolTip="Select the requirement template to apply to this record. Defaults to the project's default requirements."
                />
                <RequirementTemplateDropdownMenu
                  companyId={companyId}
                  defaultTemplate={selectedTemplate}
                  dropdownId="requirement-template-dropdown-add-to-project-form"
                  variant="primary"
                  onChange={setSelectedTemplate}
                  onUploadContract={onUploadContract}
                />
              </Box>
            </Box>
            )}
          </form>
        ) : <LoadingSpinner /> }

      </DrawerBody>
      <DrawerFooter width="100%">
        <HStack width="100%">
          <Button
            data-cy={CypressIds.insuredsSkipAddToProjectButton}
            variant="secondary"
            width="50%"
            onClick={handleSkipClick}
          >
            Skip
          </Button>
          <Button
            data-analytics-id="add-insured-to-project"
            form="addInsuredToProject"
            isDisabled={!selectedJob.id || isSubmitting}
            type="submit"
            width="50%"
          >
            Add to Project
          </Button>
        </HStack>
      </DrawerFooter>
    </>
  );
};

export default AddToProjectForm;
